import { NavLink, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  ClickAwayListener,
  Collapse,
  Divider,
  IconButton,
  Link,
  Popper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { LeadQuickActions } from '../lead-modules/LeadQuickActions';
import useFetch from '../../../api/useFetch';
import ExoAvatar from '../../../components/exo/ExoAvatar';
import { tokens } from '../../../global/theme/tokens';
import { LeadStatusSelector } from '../status/LeadStatusSelector';
import { Status } from '../../../components/special/Status';
import { Fragment, useEffect, useRef, useState } from 'react';
import { API_PATCH, DBRequest, useConfig } from '../../../api/api';
import { useTranslation } from 'react-i18next';
import { LeadNameAddress } from '../lead-modules/LeadNameAddress';
import { NewCompanyLead } from '../../../components/new/NewCompanyLead';
import ExoDialog from '../../../components/exo/ExoDialog';
import EditIcon from '@mui/icons-material/Edit';
import GroupsIcon from '@mui/icons-material/Groups';
import LaunchIcon from '@mui/icons-material/Launch';
import RenderHTML from '../../../components/preview/RenderHTML';
import { LeadMeetingHistory } from '../lead-modules/LeadMeetingHistory';
import Feedback from '../../../components/special/Feedback';
import { LeadInteractionHistory } from '../lead-modules/LeadInteractionHistory';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { NewInteraction } from '../../../components/new/NewInteraction';
import { NewInteractionNote } from '../../../components/new/NewInteractionNote';
import { updateOrCreate } from '../../../components/special/updateOrCreate';
import moment from 'moment';
import { ExoTasks } from '../../../components/exo/ExoTasks';
import { NewTask } from '../../../components/new/NewTask';
import { LeadProperties } from '../lead-modules/LeadProperties';
import { ImportantNote } from './ImportantNote';
import { validateLink } from '../../../components/special/validateLink';
import NewCompany from '../../../components/new/NewCompany';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { BoilingDiv } from '../../../components/exo/sales-object-matching/match-preview/BoilingDiv';
import { ExoSalesObjectMatching } from '../../../components/exo/ExoSalesObjectMatching';
import { SalesObjectMatch } from '../../../components/exo/sales-object-matching/match-preview/SalesObjectMatch';
import { TableCategories } from '../category/TableCategories';
import DateLabel from '../../../components/label/DateLabel';
import DateTimeLabel from '../../../components/label/DateTimeLabel';

export const ShowLeadsCompany = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const { t } = useTranslation();
  const { id } = useParams();

  const { apiData, setApiData } = useFetch(`leads/companies/${id}`);

  const [interactions, setInteractions] = useState(null);
  const [invitations, setInvitations] = useState(null);
  const [tasks, setTasks] = useState(null);

  const [openPersons, setOpenPersons] = useState(false);
  const [openLeads, setOpenLeads] = useState(false);
  const [openTopMatches, setOpenTopMatches] = useState(false);

  const [emails, setEmails] = useState([]);
  const [telephoneNumbers, setTelephoneNumbers] = useState([]);

  const {
    name,
    nameShort,
    logo,
    address,
    status,
    employeeCount,
    url,
    note,
    responsibleUser,
    persons,
    managingPersons,
    properties,
    categories,
  } = apiData?.data || {};

  const matches = apiData?.data?.matches
    ? apiData?.data?.matches.sort((a, b) => a.status - b.status)
    : null;

  useEffect(() => {
    // interactions
    DBRequest({
      config,
      path: `interactions?participantType[eq]=companyLead&participantId[eq]=${id}`,
      onResponse: setInteractions,
    });

    // invitations
    DBRequest({
      config,
      path: `event-invitations?recipientType[eq]=companyLead&recipientId[eq]=${id}`,
      onResponse: setInvitations,
    });

    // tasks
    loadUserTasks(0);
  }, [config, id]);

  function loadUserTasks(status) {
    DBRequest({
      config,
      path: `user-tasks?targetType[eq]=companyLead&targetId[eq]=${id}&status[eq]=${status}`,
      onResponse: setTasks,
    });
  }

  const [anchorEl, setAnchorEl] = useState(null);
  const [itemToChange, setItemToChange] = useState(null);
  const handleClick = (event, item) => {
    setAnchorEl(anchorEl ? null : event?.currentTarget);
    setItemToChange(item);
  };
  function handleStatusChange(status, item) {
    DBRequest({
      config,
      path: `leads/companies/${item.id}`,
      method: API_PATCH({ ...item, status }),
      onResponse: setApiData,
    });
  }

  // edit
  const [openEdit, setOpenEdit] = useState(false);
  const [editInteraction, setEditInteraction] = useState(null);
  const [editTask, setEditTask] = useState(null);

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });

  function handleFeedback(text, type) {
    setAlertState({
      alertOpen: true,
      alertText: text,
      alertType: type,
    });
  }

  // meeting change
  function updateInviations() {
    DBRequest({
      config,
      path: `event-invitations?recipientType[eq]=companyLead&recipientId[eq]=${id}`,
      onResponse: setInvitations,
    });
  }
  // task change
  function updateTasks(res) {
    const newTasks = updateOrCreate(tasks, res.data);
    setTasks(newTasks);
  }
  // interactions change
  function updateInteractions(res) {
    updateInvitations(res.data);
    const newInteractions = updateOrCreate(interactions, res.data);
    const newSortedInteractions = sortInteractions(newInteractions);

    setInteractions(newSortedInteractions);
  }

  function updateInvitations(interaction) {
    let newInvitation = invitations.data.find(
      (el) => el.event.id == interaction.sourceId
    );

    if (!newInvitation) return;

    newInvitation = {
      ...newInvitation,
      protocolsCount: newInvitation.protocolsCount + 1,
    };

    const newInvitations = updateOrCreate(invitations, newInvitation);

    setInvitations(newInvitations);
  }

  function sortInteractions(inter) {
    const sortedInteractions = inter.data.sort((a, b) =>
      moment(b.dateTime).diff(moment(a.dateTime))
    );
    return { ...inter, data: sortedInteractions };
  }

  function handleLoadInteractions(type) {
    setInteractions(null);

    const path = !type
      ? `interactions?participantType[eq]=companyLead&participantId[eq]=${id}`
      : `interactions?participantType[eq]=companyLead&participantId[eq]=${id}&type[eq]=${type}`;

    DBRequest({
      config,
      path,
      onResponse: setInteractions,
    });
  }

  useEffect(() => {
    calcEmailList();
    calcTelephoneNumbersList();
  }, [apiData?.data.emails, apiData?.data.persons]);

  function calcEmailList() {
    let emails = [];
    persons?.forEach((element) => {
      if (element.emails)
        element.emails.forEach((email) => {
          emails.push({
            ...email,
            emailType: `${element.firstName} ${element.lastName} - ${email.emailType}`,
          });
        });
    });
    managingPersons?.forEach((element) => {
      if (element.emails)
        element.emails.forEach((email) => {
          emails.push({
            ...email,
            emailType: `${element.firstName} ${element.lastName} - ${email.emailType}`,
          });
        });
    });

    setEmails(emails);
  }

  function calcTelephoneNumbersList() {
    let telephoneNumbers = [];
    persons?.forEach((element) => {
      if (element.telephoneNumbers)
        element.telephoneNumbers.forEach((number) => {
          telephoneNumbers.push({
            ...number,
            phoneType: `${element.firstName} ${element.lastName} - ${number.phoneType}`,
          });
        });
    });
    managingPersons?.forEach((element) => {
      if (element.telephoneNumbers)
        element.telephoneNumbers.forEach((number) => {
          telephoneNumbers.push({
            ...number,
            phoneType: `${element.firstName} ${element.lastName} - ${number.phoneType}`,
          });
        });
    });

    setTelephoneNumbers(telephoneNumbers);
  }

  // lead to customer
  const [openLeadToCustomer, setOpenLeadToCustomer] = useState(false);
  function handleCreatedCustomer(res) {
    setApiData({ ...apiData, data: { ...apiData.data, company: res.data } });
  }
  const company = apiData?.data.company;

  const matchesCount = matches
    ? matches?.filter((match) => match?.element && match?.accessPoint).length
    : 0;

  const [currentType, setCurrentType] = useState(0);

  function handleTypeChange(e) {
    if (e.target.value === currentType) return;
    setCurrentType(e.target.value);
  }

  return (
    <Fragment key={id}>
      <Box className="pt-0 md:pt-10 flex flex-col xl:flex-row gap-2 xl:h-full md:overflow-y-auto xl:overflow-hidden w-full min-w-0">
        <Box
          sx={{ bgcolor: colors.glass }}
          className=" rounded-lg backdrop-blur-md p-4 relative xl:min-w-[400px] xl:max-w-[450px] xl:flex-shrink-0 overflow-y-auto md:h-full lg:h-auto md:min-h-full lg:min-h-auto"
        >
          <IconButton
            sx={{ position: 'absolute' }}
            className="top-2 right-2"
            onClick={() => setOpenEdit(true)}
          >
            <EditIcon />
          </IconButton>
          <LeadNameAddress
            name={nameShort || name}
            subName={nameShort ? name : null}
            address={address}
            source={apiData?.data}
            image={logo}
            telephoneNumbers={apiData?.data.telephoneNumbers.concat(
              telephoneNumbers
            )}
            emails={apiData?.data.emails.concat(emails)}
            sourceType="companyLead"
            sourceId={id}
            onNewInteraction={updateInteractions}
          />
          <Box className="pt-2 pb-3">
            <Divider />
          </Box>
          <Box className="w-full grid grid-cols-2 gap-2">
            <Box className="pb-2 w-full">
              <Typography fontWeight={600}>{t('Lead Status')}</Typography>
              <Status
                text={status?.label || t('No Status')}
                variant={status?.variant || 'base'}
                statusColor={status?.color}
                onClick={(e) => handleClick(e, apiData?.data)}
                disableMobile
                fullWidth
              />
            </Box>

            {categories?.length > 0 && (
              <Box className="pb-2 w-full">
                <Typography fontWeight={600}>{t('Categories')}</Typography>
                <TableCategories categories={categories} />
              </Box>
            )}
          </Box>

          <LeadQuickActions
            isLoading={!apiData}
            name={nameShort || name}
            data={apiData}
            type="companyLead"
            typePlural="companyLeads"
            onFeedback={handleFeedback}
            onMeetingChange={updateInviations}
            onInteractionChange={updateInteractions}
            onTaskChange={updateTasks}
          />
          {company ? (
            <Box className="pt-[10px]">
              <Typography fontWeight={500} className="opacity-50">
                {t('Company')}
              </Typography>
              <NavLink to={`/companies/${company.id}`}>
                <Box className="flex items-center gap-2">
                  <ExoAvatar
                    size="40px"
                    picture={company?.logo?.temporaryUrl}
                    name={company.nameShort || company.name}
                    type="company"
                  />
                  <Typography>{company.nameShort || company.name}</Typography>
                </Box>
              </NavLink>
            </Box>
          ) : (
            <Box className="w-full pt-[10px]">
              <Button
                className="w-full"
                onClick={() => setOpenLeadToCustomer(true)}
                sx={{
                  bgcolor: colors.card,
                  ':hover': { bgcolor: colors.selected },
                }}
              >
                {t('Convert to customer')}
                <AutoAwesomeIcon
                  className="ml-[5px]"
                  sx={{ fontSize: '16px' }}
                />
              </Button>
            </Box>
          )}
          <Box className="py-3">
            <Divider />
          </Box>
          <Box className="w-full grid grid-cols-2 gap-2">
            <Box>
              {managingPersons && managingPersons.length > 0 && (
                <Box className="pb-2">
                  <Typography fontWeight={600}>
                    {t('Business management')}
                  </Typography>
                  <Box className="flex flex-col gap-1">
                    {managingPersons?.map((person) => (
                      <NavLink
                        to={`/leads/persons/${person?.id}`}
                        key={person.id}
                      >
                        <Box className=" pl-2 flex flex-row justify-start items-center gap-2 whitespace-nowrap">
                          <ExoAvatar
                            name={`${person?.lastName} ${person?.firstName}`}
                            size="35px"
                          />
                          <Box className="overflow-hidden">
                            <Typography
                              noWrap
                              className="max-w-full text-ellipsis overflow-hidden"
                            >
                              {`${person?.lastName} ${person?.firstName}`}
                            </Typography>
                            <Typography
                              fontSize={10}
                              className="opacity-60 truncate max-w-full"
                            >
                              {person.position}
                            </Typography>
                          </Box>
                        </Box>
                      </NavLink>
                    ))}
                  </Box>
                </Box>
              )}
              {persons && persons.length > 0 && (
                <Box className="flex flex-col gap-1 pb-2">
                  <Button
                    sx={{ padding: 0, justifyContent: 'space-between' }}
                    onClick={() => setOpenPersons(!openPersons)}
                  >
                    <Typography fontWeight={600} className="pb-1">
                      {`${t('Employee')} (${persons.length})`}
                    </Typography>
                    <ArrowDropDownIcon
                      sx={{
                        transform: `rotate(${openPersons ? 180 : 0}deg)`,
                      }}
                    />
                  </Button>
                  <Collapse in={openPersons}>
                    <Box className="flex flex-col gap-1">
                      {persons?.map((person) => (
                        <NavLink
                          to={`/leads/persons/${person?.id}`}
                          key={person.id}
                        >
                          <Box className=" pl-2 flex flex-row justify-start items-center gap-2 whitespace-nowrap">
                            <ExoAvatar
                              name={`${person?.lastName} ${person?.firstName}`}
                              size="35px"
                            />
                            <Box>
                              <Typography>{`${person?.lastName} ${
                                person?.firstName || ''
                              }`}</Typography>
                              <Typography fontSize={10} className=" opacity-60">
                                {person.position}
                              </Typography>
                            </Box>
                          </Box>
                        </NavLink>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              )}
            </Box>
            <Box>
              <Typography fontWeight={600}>{t('Info')}</Typography>
              <Typography className="pb-1 pl-2">
                <GroupsIcon className="mr-2" />
                <b>{employeeCount}</b>
                {` ${t('Employee')}`}
              </Typography>

              <Typography className="pl-2" sx={{ wordBreak: 'break-word' }}>
                {url && (
                  <a href={validateLink(url)} target="_blank">
                    <LaunchIcon className="mr-2" />
                    {url}
                  </a>
                )}
              </Typography>
            </Box>
          </Box>

          <Box className="flex flex-col gap-1 pb-2">
            <Button
              sx={{ padding: 0, justifyContent: 'space-between' }}
              onClick={() => setOpenLeads(!openLeads)}
            >
              <Typography fontWeight={600} className="pb-1">
                {`${t('Sales Objects')} (${matchesCount})`}
              </Typography>
              <ArrowDropDownIcon
                sx={{
                  transform: `rotate(${openLeads ? 180 : 0}deg)`,
                }}
              />
            </Button>
            <Collapse in={openLeads}>
              <Box className="flex flex-col gap-1">
                {matches?.map((match) => {
                  /*if (!match?.element || !match?.accessPoint) {
                    // Skip rendering if either element or accessPoint is missing
                    return null;
                  }*/

                  if (match?.status == 2 && match?.element)
                    return (
                      <>
                        <NavLink
                          to={`/sales-objects/${match.element.id}`}
                          key={match.element.id}
                        >
                          <Box className="pl-2 flex flex-row justify-start items-center gap-2 whitespace-nowrap">
                            <ExoAvatar
                              picture={match.element.image?.temporaryUrl}
                              name={`${match.element.lastName} ${match.element.firstName}`}
                              size="35px"
                            />
                            <Box>
                              <Typography>{match.element.title}</Typography>
                              <Typography
                                fontSize={10}
                                className="opacity-80"
                                color="error"
                              >
                                {t('Aborted')}
                              </Typography>
                            </Box>
                          </Box>
                        </NavLink>
                      </>
                    );

                  if (
                    match?.status == 1 &&
                    match?.element &&
                    match?.accessPoint
                  )
                    return (
                      <>
                        <NavLink
                          to={`/sales-objects/${match.element.id}`}
                          key={match.element.id}
                        >
                          <Box className="flex justify-between">
                            <Box className="pl-2 flex flex-row justify-start items-center gap-2 whitespace-nowrap">
                              <ExoAvatar
                                picture={match.element.image?.temporaryUrl}
                                name={`${match.element.lastName} ${match.element.firstName}`}
                                size="35px"
                              />
                              <Box>
                                <Typography>{match.element.title}</Typography>
                                <Typography
                                  fontSize={10}
                                  className="opacity-60"
                                >
                                  {`${match.accessPoint.accessLevel} - ${match?.accessPoint?.level?.title}`}
                                </Typography>
                              </Box>
                            </Box>
                            {match?.accessPoint?.levelUpdatedAt && (
                              <Tooltip title={t('On this level since')}>
                                <Typography
                                  className="opacity-50"
                                  fontSize={11}
                                >
                                  <DateTimeLabel
                                    dateTime={
                                      match?.accessPoint?.levelUpdatedAt
                                    }
                                  />
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                        </NavLink>
                      </>
                    );

                  return;
                })}
                <Tooltip title={t('Explore Top Matches')}>
                  <Box className={' flex rounded-lg '}>
                    <Button
                      onClick={() => setOpenTopMatches(true)}
                      className=" opacity-40 w-full flex "
                      variant="contained"
                      color="secondary"
                      disableElevation
                    >
                      <AddIcon
                        className=" opacity-30"
                        sx={{
                          fontSize: '20px',
                          marginRight: 'Top Matches' ? '10px' : '0px',
                        }}
                      />
                    </Button>
                  </Box>
                </Tooltip>
              </Box>
            </Collapse>
          </Box>

          <ExoDialog
            open={openTopMatches}
            onClose={() => setOpenTopMatches(false)}
            limitWidth
          >
            <Box className="w-full max-w-[800px] h-full flex flex-col">
              <Box className="flex items-center justify-between w-full mb-2 relative">
                <Typography variant="h4" className="pb-1">
                  {t('Matches')}
                </Typography>
                <ToggleButtonGroup
                  sx={{
                    '& button': {
                      padding: '3px 6px',
                    },
                  }}
                  size="small"
                  value={currentType}
                  exclusive
                  onChange={handleTypeChange}
                  aria-label="Matching Status"
                  className="backdrop-blur-lg"
                >
                  <ToggleButton value={0}>{t('Matches')}</ToggleButton>
                  <ToggleButton value={1}>{t('Active')}</ToggleButton>
                  <ToggleButton value={2}>{t('Declined')}</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Typography className="pb-4 opacity-70">
                {t(
                  'A list of all hits that were determined based on the purchasing parameters stored in Leads'
                )}
              </Typography>
              <ExoSalesObjectMatching
                source="companyLead"
                sourceId={id}
                status={currentType}
              />
            </Box>
          </ExoDialog>

          <ImportantNote
            apiData={apiData}
            setApiData={setApiData}
            path={`leads/companies/${id}`}
          />
          <LeadProperties properties={properties} />
          {responsibleUser && (
            <Box className="pt-10">
              <Typography fontWeight={600}>
                {t('Responsible Employee')}
              </Typography>
              <NavLink to={`/employees/${responsibleUser?.id}`}>
                <Box className=" pl-2 flex flex-row justify-start items-center gap-2 whitespace-nowrap">
                  <ExoAvatar
                    picture={responsibleUser?.picture?.thumbnailUrl}
                    name={`${responsibleUser?.lastName} ${responsibleUser?.firstName}`}
                    size="35px"
                  />
                  {`${responsibleUser?.lastName} ${responsibleUser?.firstName}`}
                </Box>
              </NavLink>
            </Box>
          )}
        </Box>
        <Box
          sx={{ bgcolor: colors.glass }}
          className=" rounded-lg backdrop-blur-md pl-3 pr-2 py-2 relative w-full xl:w-auto xl:flex-1 xl:h-full flex flex-col min-w-0"
        >
          <Typography fontWeight={600} className="pb-2">
            {t('History')}
          </Typography>
          <LeadInteractionHistory
            leadType="companyLead"
            leadId={id}
            interactions={interactions}
            onEdit={(e) => setEditInteraction(e)}
            setInteractions={setInteractions}
            onFeedback={handleFeedback}
            loadData={handleLoadInteractions}
          />
        </Box>
        <Box className="w-full xl:w-auto xl:flex-1 min-w-0 grid grid-rows-2 gap-2">
          <Box
            sx={{ bgcolor: colors.glass }}
            className=" rounded-lg backdrop-blur-md pl-3 pr-2 py-2 relative w-full xl:h-full flex flex-col"
          >
            <Typography fontWeight={600} className="pb-4">
              {t('Tasks')} {tasks && `(${tasks.data.length})`}
            </Typography>
            <Box className="flex flex-col gap-1 overflow-y-auto max-h-[500px] md:max-h-[9999px] md:h-full">
              <ExoTasks
                tasks={tasks}
                onChange={setTasks}
                noNew
                toggelSx={{ position: 'absolute', top: 6, right: 6 }}
                onEdit={setEditTask}
                onStatusChange={loadUserTasks}
                onFeedback={handleFeedback}
                targetId={apiData?.data.id}
                targetType="companyLead"
              />
            </Box>
          </Box>

          <Box
            sx={{ bgcolor: colors.glass }}
            className=" rounded-lg backdrop-blur-md pl-3 pr-2 py-2 relative w-full xl:h-full flex flex-col"
          >
            <Typography fontWeight={600} className="pb-2">
              {t('Meetings')}
            </Typography>
            <LeadMeetingHistory
              invitations={invitations}
              setInvitations={setInvitations}
              onInteractionChange={updateInteractions}
              onFeedback={handleFeedback}
            />
          </Box>
        </Box>
      </Box>

      <ExoDialog open={openEdit} onClose={() => setOpenEdit(false)} limitWidth>
        <NewCompanyLead
          startData={apiData?.data}
          onUpdate={setApiData}
          onCreate={setApiData}
          onClose={() => setOpenEdit(false)}
          onFeedback={handleFeedback}
        />
      </ExoDialog>

      <ExoDialog
        open={editInteraction && editInteraction.type !== 'note'}
        onClose={() => setEditInteraction(null)}
        limitWidth
      >
        <NewInteraction
          startData={editInteraction}
          onClose={() => setEditInteraction(null)}
          onCreate={updateInteractions}
          onUpdate={updateInteractions}
          onFeedback={handleFeedback}
          textBlockType={'lead'}
        />
      </ExoDialog>

      <ExoDialog
        open={
          editInteraction &&
          (editInteraction.type === 'note' || editInteraction.type === 'email')
        }
        onClose={() => setEditInteraction(null)}
        limitWidth
      >
        <NewInteractionNote
          startData={editInteraction !== 'note' ? editInteraction : {}}
          onClose={() => setEditInteraction(null)}
          onCreate={updateInteractions}
          onUpdate={updateInteractions}
          onFeedback={handleFeedback}
          sourceType={editInteraction?.type}
          textBlockType={'lead'}
        />
      </ExoDialog>

      <ExoDialog
        open={Boolean(editTask)}
        onClose={() => setEditTask(null)}
        limitWidth
      >
        <NewTask
          startData={editTask}
          onClose={() => setEditTask(null)}
          onCreate={updateTasks}
          onChange={updateTasks}
          onFeedback={handleFeedback}
          targetId={apiData?.data.id}
          targetType={'companyLead'}
        />
      </ExoDialog>

      <ExoDialog
        open={openLeadToCustomer}
        onClose={() => setOpenLeadToCustomer(false)}
        limitWidth
      >
        <NewCompany
          fullWidth
          startData={{
            ...apiData?.data,
            id: undefined,
            leadId: apiData?.data.id,
          }}
          onCreate={handleCreatedCustomer}
          onUpdate={handleCreatedCustomer}
          onClose={() => setOpenLeadToCustomer(false)}
        />
      </ExoDialog>

      <LeadStatusSelector
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onStatusChange={(status) =>
          handleStatusChange(status, {
            id: itemToChange.id,
          })
        }
      />
      <Feedback setState={setAlertState} state={alertState} />
    </Fragment>
  );
};
